/**
 * MeterBank
 *
 * Manages many participant level meters from a single rAF loop. Each meter is
 * rendered to its own canvas, but a single tick drives all of them so we never
 * have N animation frames competing.
 *
 * Visual model: segmented LED bar from FLOOR_DB to 0 dBFS.
 *   - Green:  FLOOR_DB .. -18 dBFS
 *   - Yellow: -18      .. -6
 *   - Red:    -6       .. 0
 *
 * Animation model:
 *   - rms: snappy attack toward target, slow release (~20 dB/sec)
 *   - peak: discrete peak-hold marker that holds then decays
 *
 * The bar value rendered is the smoothed RMS; the peak marker tracks the
 * (also smoothed-attack) peak. Both are stored in dBFS internally.
 */

const FLOOR_DB = -60;
const TOP_DB = 0;
const SEGMENT_COUNT = 36;
const SEGMENT_GAP = 2; // CSS pixels

// dB thresholds for colour zones
const YELLOW_DB = -18;
const RED_DB = -6;
const TARGET_DB = -12; // recommended streaming peak target

// Animation tuning (per second; will be scaled by frame dt)
const ATTACK_RATE = 0.985; // fraction of gap closed per ms when rising
const RELEASE_DB_PER_SEC = 20; // bar release rate (gentle)
const PEAK_RELEASE_DB_PER_SEC = 36; // peak marker decay rate
const PEAK_HOLD_MS = 800; // dwell time before peak starts to decay
const STALE_MS = 4000; // mark a row stale if no sample arrives

type MeterState = {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  dpr: number;
  cssW: number;
  cssH: number;

  // Smoothed values shown on screen
  rmsDb: number;
  peakDb: number;
  peakSetAt: number;

  // Latest target values from network samples
  targetRmsDb: number;
  targetPeakDb: number;
  lastSampleAt: number;
};

export class MeterBank {
  private meters = new Map<string, MeterState>();
  private rafId: number | null = null;
  private lastFrameTs = 0;
  private resizeObserver: ResizeObserver;

  constructor() {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const e of entries) {
        const id = (e.target as HTMLElement).dataset.meterId;
        if (!id) continue;
        const m = this.meters.get(id);
        if (m) this.resize(m);
      }
    });
    this.tick = this.tick.bind(this);
  }

  attach(id: string, canvas: HTMLCanvasElement) {
    canvas.dataset.meterId = id;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("2D canvas context unavailable");

    const state: MeterState = {
      canvas,
      ctx,
      dpr: window.devicePixelRatio || 1,
      cssW: 0,
      cssH: 0,
      rmsDb: FLOOR_DB,
      peakDb: FLOOR_DB,
      peakSetAt: 0,
      targetRmsDb: FLOOR_DB,
      targetPeakDb: FLOOR_DB,
      lastSampleAt: 0,
    };
    this.meters.set(id, state);
    this.resize(state);
    this.resizeObserver.observe(canvas);

    if (this.rafId === null) {
      this.lastFrameTs = performance.now();
      this.rafId = requestAnimationFrame(this.tick);
    }
  }

  detach(id: string) {
    const m = this.meters.get(id);
    if (!m) return;
    this.resizeObserver.unobserve(m.canvas);
    this.meters.delete(id);
    if (this.meters.size === 0 && this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  update(id: string, sample: { rms: number; peak: number }) {
    const m = this.meters.get(id);
    if (!m) return;
    m.targetRmsDb = clamp(sample.rms, FLOOR_DB, TOP_DB);
    const newPeak = clamp(sample.peak, FLOOR_DB, TOP_DB);
    // Peak only resets upward immediately; if the incoming peak exceeds what
    // we're already holding, take it. Otherwise the natural decay handles it.
    if (newPeak > m.targetPeakDb) {
      m.targetPeakDb = newPeak;
      m.peakSetAt = performance.now();
    }
    m.lastSampleAt = performance.now();
  }

  reset(id: string) {
    const m = this.meters.get(id);
    if (!m) return;
    m.rmsDb = FLOOR_DB;
    m.peakDb = FLOOR_DB;
    m.targetRmsDb = FLOOR_DB;
    m.targetPeakDb = FLOOR_DB;
  }

  /** Current displayed dBFS for a participant — for the numeric readout. */
  currentDb(id: string): number {
    return this.meters.get(id)?.rmsDb ?? FLOOR_DB;
  }

  isStale(id: string, now = performance.now()): boolean {
    const m = this.meters.get(id);
    if (!m) return false;
    if (m.lastSampleAt === 0) return false;
    return now - m.lastSampleAt > STALE_MS;
  }

  destroy() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.resizeObserver.disconnect();
    this.meters.clear();
  }

  private resize(m: MeterState) {
    const rect = m.canvas.getBoundingClientRect();
    const cssW = Math.max(1, Math.floor(rect.width));
    const cssH = Math.max(1, Math.floor(rect.height));
    const dpr = window.devicePixelRatio || 1;
    if (m.cssW === cssW && m.cssH === cssH && m.dpr === dpr) return;
    m.cssW = cssW;
    m.cssH = cssH;
    m.dpr = dpr;
    m.canvas.width = Math.floor(cssW * dpr);
    m.canvas.height = Math.floor(cssH * dpr);
    m.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private tick(ts: number) {
    const dt = Math.min(64, ts - this.lastFrameTs); // ms, clamp big gaps
    this.lastFrameTs = ts;
    for (const [id, m] of this.meters) {
      this.advance(m, dt, ts);
      this.draw(m, id, ts);
    }
    this.rafId = requestAnimationFrame(this.tick);
  }

  private advance(m: MeterState, dt: number, ts: number) {
    // RMS bar
    if (m.targetRmsDb > m.rmsDb) {
      const attackFactor = 1 - Math.pow(1 - ATTACK_RATE, dt / 16.67);
      m.rmsDb += (m.targetRmsDb - m.rmsDb) * attackFactor;
    } else {
      const drop = (RELEASE_DB_PER_SEC * dt) / 1000;
      m.rmsDb = Math.max(m.targetRmsDb, m.rmsDb - drop);
    }

    // Peak marker
    if (m.targetPeakDb > m.peakDb) {
      m.peakDb = m.targetPeakDb;
    } else if (ts - m.peakSetAt > PEAK_HOLD_MS) {
      const drop = (PEAK_RELEASE_DB_PER_SEC * dt) / 1000;
      m.peakDb = Math.max(FLOOR_DB, m.peakDb - drop);
      m.targetPeakDb = m.peakDb;
    }

    if (m.rmsDb < FLOOR_DB) m.rmsDb = FLOOR_DB;
  }

  private draw(m: MeterState, _id: string, _ts: number) {
    const { ctx, cssW, cssH } = m;
    ctx.fillStyle = "#161b22";
    ctx.fillRect(0, 0, cssW, cssH);

    const padX = 4;
    const padY = 5;
    const innerW = cssW - padX * 2;
    const innerH = cssH - padY * 2;
    if (innerW <= 0 || innerH <= 0) return;

    const segW = (innerW - SEGMENT_GAP * (SEGMENT_COUNT - 1)) / SEGMENT_COUNT;
    const valNorm = normalize(m.rmsDb);
    const peakNorm = normalize(m.peakDb);
    const litCount = Math.round(valNorm * SEGMENT_COUNT);
    const peakIdx = m.peakDb > FLOOR_DB
      ? Math.min(SEGMENT_COUNT - 1, Math.round(peakNorm * SEGMENT_COUNT) - 1)
      : -1;
    const targetIdx = Math.round(normalize(TARGET_DB) * SEGMENT_COUNT) - 1;

    for (let i = 0; i < SEGMENT_COUNT; i++) {
      const segDbCenter = FLOOR_DB + ((i + 0.5) / SEGMENT_COUNT) * (TOP_DB - FLOOR_DB);
      const x = padX + i * (segW + SEGMENT_GAP);
      const y = padY;

      const baseColor = colorForDb(segDbCenter);
      const lit = i < litCount;
      const isPeak = i === peakIdx;
      const isTarget = i === targetIdx;

      // Off-state: very dim version of the colour
      ctx.fillStyle = lit ? baseColor : dimColor(baseColor);
      roundRect(ctx, x, y, segW, innerH, 1.5);
      ctx.fill();

      if (isPeak) {
        // White-ish peak indicator on top of the segment colour
        ctx.fillStyle = "#ffffff";
        roundRect(ctx, x, y, segW, innerH, 1.5);
        ctx.fill();
      } else if (isTarget && !lit) {
        // Faint target tick on the unlit zone
        ctx.fillStyle = "rgba(255,255,255,0.18)";
        ctx.fillRect(x + segW / 2 - 0.5, y + innerH - 4, 1, 4);
      }
    }
  }
}

// ── Helpers ──────────────────────────────────────────────────────

function normalize(db: number): number {
  if (db <= FLOOR_DB) return 0;
  if (db >= TOP_DB) return 1;
  return (db - FLOOR_DB) / (TOP_DB - FLOOR_DB);
}

function clamp(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return lo;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

function colorForDb(db: number): string {
  if (db >= RED_DB) return "#ef4444"; // red
  if (db >= YELLOW_DB) return "#fbbf24"; // amber
  return "#34d399"; // green
}

function dimColor(hex: string): string {
  // Very low alpha overlay-style — but since the canvas is alpha:false, fake it.
  switch (hex) {
    case "#ef4444":
      return "#2a191a";
    case "#fbbf24":
      return "#2a2419";
    case "#34d399":
      return "#1a2a23";
  }
  return "#1a1f26";
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export { FLOOR_DB, TOP_DB, TARGET_DB };
